//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import moment, { Moment } from 'moment-timezone';

import TimeZoneId from '@models/TimeZoneId';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { getDatesInRange } from '@utils/getDatesInRange';
import { WeekDayAbbr, getDayOfWeek, momentFromDate } from '@utils/moment';
import { Opaque } from '@utils/opaque';

export const DATE_STRING_FORMAT = 'YYYY-MM-DD';

/**
 * A String representing a Date of the format YYYY-MM-DD
 */
type DateString = Opaque<`${number}-${number}-${number}`, 'DateString'>;

/**
 * Creates a DateString from a year, month, and day.
 *
 * @param year The Year
 * @param month The Month. 1-indexed (January is 1)
 * @param day The Day of the month.
 */
function DateStringFactory(
  year: number,
  month: number,
  day: number,
): DateString {
  // TODO: Validate day/month/year combination is valid?
  return moment({
    year,
    // Months are zero-indexed in moment input.
    month: month - 1,
    day,
  }).format(DATE_STRING_FORMAT) as unknown as DateString;
}
const DateString = Object.assign(DateStringFactory, {
  /**
   * Creates a Date String from a given timestamp.
   * Timezone is required. Even if a Timestamp is in UTC, the date it represents depends on that timezone of the user.
   * @param timestamp A datetime timestamp.
   * @param timeZone a TimeZone Identifier to use for calculating the date.
   */
  fromTimestamp(timestamp: number | Date, timeZone: TimeZoneId): DateString {
    return momentFromDate(timestamp, timeZone).format(
      DATE_STRING_FORMAT,
    ) as DateString;
  },

  /**
   * Converts a string representing a date with an alternative format (ex: MM-DD-YY)
   * into a DateString tinytype string in the correct format.
   */
  fromString(dateStr: string, parseFormat: string) {
    /**
     * TODO: Use a regex here to verify that the format string validly would represent
     * a date in moment, i.e. that is has D|DD, M|MM, and YY|YYYY in any order with
     * any delimiter. For now, though, borrow the regex from the format method below
     * to at least make sure we're not passing in strings that deal with non-date formatters.
     */
    if (/[AaHhkmsSzZxX]/.test(parseFormat)) {
      throw new Error('Unsupported format specifier for Date Range');
    }

    return moment(dateStr, parseFormat).format(
      DATE_STRING_FORMAT,
    ) as DateString;
  },

  /**
   * Convert a UnisonDateRange into a pair of DateStrings for the daterange it represents
   * @param dateRange
   */
  fromUnisonDateRange(dateRange: IUnisonDateRange) {
    // N.B. Unison date ranges are built using the BROWSER timezone.
    // These timestamps are set to start-of-day in the browser timezone, so we
    // need to use the browser timezone to convert them into DateStrings
    const fromBrowserDate = (d: Date) =>
      moment(d).format(DATE_STRING_FORMAT) as DateString;
    return {
      startDate: fromBrowserDate(dateRange.startDate),
      endDate: fromBrowserDate(dateRange.endDate),
    } as const;
  },

  /**
   * Get a list of all the date strings in a unison date range
   * @param dateRange A Unison Date Range object
   */
  inUnisonDateRange(dateRange: IUnisonDateRange): DateString[] {
    // N.B. Unison date ranges are built using the BROWSER timezone.
    // These timestamps are set to start-of-day in the browser timezone, so we
    // need to use the browser timezone to convert them into DateStrings
    const fromBrowserDate = (d: Date) =>
      moment(d).format(DATE_STRING_FORMAT) as DateString;
    return getDatesInRange(dateRange.startDate, dateRange.endDate).map(
      fromBrowserDate,
    );
  },

  /**
   * Create a moment at start-of-day in the given timezone.
   * @param date The date string.
   * @param timeZone Target timezone.
   */
  toMoment(date: DateString, timeZone: TimeZoneId): Moment {
    return moment.tz(date, DATE_STRING_FORMAT, timeZone);
  },
  /**
   * Create a date at start-of-day in the given timezone.
   * @param date The date string.
   * @param timeZone Target timezone.
   */
  toDate(date: DateString, timeZone: TimeZoneId): Date {
    return DateString.toMoment(date, timeZone).toDate();
  },
  /**
   * Adds the given offset to the given date and returns the result.
   * The offset can be positive or negative.
   */
  addDays(date: DateString, offset: number): DateString {
    return DateString.applyMomentOperation(date, 'add', offset, 'd');
  },
  /**
   * Adds one day to the given date and returns the result.
   */
  addOneDay(date: DateString): DateString {
    return DateString.addDays(date, 1);
  },
  /**
   * Adds the given month offset to the given date and returns the result.
   * The offset can be positive or negative.
   */
  addMonths(date: DateString, offset: number): DateString {
    return DateString.applyMomentOperation(date, 'add', offset, 'M');
  },
  /**
   * Adds the given month offset to the given date and returns the result.
   * The offset can be positive or negative.
   */
  addYears(date: DateString, offset: number): DateString {
    return DateString.applyMomentOperation(date, 'add', offset, 'y');
  },
  /**
   * Get a DateString for the current date, per the given timezone or the
   * browser timezone if none is given.
   */
  getToday(timeZone: TimeZoneId): DateString {
    return DateString.fromTimestamp(new Date(), timeZone);
  },
  /**
   * Gets the offset between the two given dates. If a > b, the offset will
   * be positive. If b > a, the offset will be negative.
   */
  getDayOffset(a: DateString, b: DateString): number {
    const aMoment = DateString.toMoment(a, TimeZoneId.Utc);
    const bMoment = DateString.toMoment(b, TimeZoneId.Utc);
    return bMoment.diff(aMoment, 'days');
  },

  /**
   * Compare two datestrings.
   * Compatible with Array.prototype.sort() as a compare function.
   * @returns 0 if dates are the same. Negative if a is before b. Positive if a is after b.
   */
  compare(a: DateString, b: DateString): number {
    return DateString.getDayOffset(b, a);
  },

  /**
   * Check if a date is within a range, inclusively.
   */
  isWithin(
    date: DateString,
    { minDate, maxDate }: { minDate?: DateString; maxDate?: DateString },
  ): boolean {
    return (
      (!minDate || DateString.compare(minDate, date) <= 0) &&
      (!maxDate || DateString.compare(date, maxDate) <= 0)
    );
  },

  /**
   * Returns a formatted string from a DateString
   *
   * Only allows Calendar Date related format specifiers
   *
   * @see Moment Format docs: https://momentjs.com/docs/#/displaying/format/
   */
  format(date: DateString, format: string): string {
    // Don't allow time-related format specifiers!
    if (/[AaHhkmsSzZxX]/.test(format)) {
      throw new Error('Unsupported format specifier for Date Range');
    }
    const dateMoment = DateString.toMoment(date, TimeZoneId.Utc);
    return dateMoment.format(format);
  },

  /**
   * Get an array of all the DateStrings in the range from start DateString to end DateString.
   * Time of day will be based on the start date time of day.
   */
  getDateStringsInRange(start: DateString, end: DateString): DateString[] {
    const length = DateString.getDayOffset(start, end);
    return Array(length)
      .fill(0)
      .map((val, index) => {
        return DateString.addDays(start, index);
      });
  },

  /**
   * Applies one of the two permitted moment operations (add and subtract) to
   * the given date string. This allows for easy manipulation of the given date
   * string with Calendar Date related operations.
   *
   * Permitted operation amounts: day (d), week (w), month (M), year (y)
   */
  applyMomentOperation(
    date: DateString,
    operationName: 'add' | 'subtract',
    offset: number,
    amount: 'd' | 'w' | 'M' | 'y',
  ): DateString {
    const dateMoment = DateString.toMoment(date, TimeZoneId.Utc);
    const alteredMoment = dateMoment[operationName](offset, amount);
    return DateString.fromTimestamp(alteredMoment.toDate(), TimeZoneId.Utc);
  },

  /**
   * Applies one of the permitted setter operations to the given date string.
   * This allows for easy manipulation of the day, month, and year of the
   * given date string.
   *
   * Permitted setters: day of week (day), day of month (date), month, year
   */
  applyMomentSetter(
    date: DateString,
    setterName: 'day' | 'date' | 'month' | 'year',
    num: number,
  ): DateString {
    const dateMoment = DateString.toMoment(date, TimeZoneId.Utc);
    const alteredMoment: Moment = dateMoment[setterName](num);
    return DateString.fromTimestamp(alteredMoment.toDate(), TimeZoneId.Utc);
  },

  parse(date: DateString): {
    day: number;
    month: number;
    year: number;
  } {
    const [year, month, day] = date.split('-').map((num) => parseInt(num, 10));

    return {
      year: year ?? 0,
      month: month ?? 0,
      day: day ?? 0,
    };
  },

  getDayOfWeek(date: DateString): WeekDayAbbr {
    return getDayOfWeek(DateString.toMoment(date, TimeZoneId.Utc));
  },
  getDaysInMonth(date: DateString): number {
    return DateString.toMoment(date, TimeZoneId.Utc).daysInMonth();
  },
});

export default DateString;
